import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Mail, CheckCircle } from 'lucide-react';
import { authService } from './services/authService';
import AuthLayout from './components/auth/AuthLayout';
import AuthCard from './components/auth/AuthCard';
import Button from './components/ui/Button';
import logger, { safeErrorEventDetails } from './utils/logger';

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      logger.errorEvent('password_reset_invalid_input', { field: 'email' });
      setError('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    try {
      await authService.requestPasswordReset(email);
      setStatus('success');
    } catch (err) {
      logger.errorEvent('password_reset_request_failed', safeErrorEventDetails(err));
      setError('Something went wrong. Please try again.');
      setStatus('idle');
    }
  };

  return (
    <AuthLayout>
        <AuthCard>
          {status === 'success' ? (
            <div className="auth-heading">
              <div className="auth-status-icon success">
                <CheckCircle className="h-10 w-10 text-[var(--color-text-primary)]" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Check Your Email</h2>
              <p className="text-[var(--color-text-secondary)]">
                If an account exists, we sent a password reset link.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="mt-6"
              >
                Back to Sign In
              </Button>
            </div>
          ) : (
            <>
              <div className="auth-heading mb-6">
                <div className="auth-status-icon">
                  <Mail className="h-10 w-10 text-[var(--color-text-primary)]" />
                </div>
                <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Forgot Password?</h2>
                <p className="text-[var(--color-text-secondary)]">Enter your email to receive a reset link.</p>
              </div>

              {error && (
                <div className="auth-error" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="reset-email" className="mb-2 block text-sm font-semibold text-[var(--color-text-secondary)]">Email</label>
                  <input
                    id="reset-email" type="email" autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-4 py-3 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] transition-colors duration-150"
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  loading={status === 'loading'}
                  loadingLabel="Sending link…"
                  fullWidth
                >
                  Send Reset Link
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  fullWidth
                >
                  ← Back to login
                </Button>
              </form>
            </>
          )}
        </AuthCard>
    </AuthLayout>
  );
}

export default ForgotPassword;
